/**
 * 드래그 가능한 패널 컴포넌트
 * - 타이틀바를 통한 드래그 앤 드롭 기능
 * - URL 입력 및 iframe으로 로드 기능
 * - 상하좌우 패널 크기 조절 기능
 * - URL 입력창 토글 기능
 * - 설정 저장 및 복원 기능
 */

/**
 * 드래그 가능한 패널 클래스
 */
class DraggablePanel {
  /**
   * 패널 생성자
   * @param {Object} options - 패널 설정 옵션
   * @param {string} options.title - 패널 제목
   * @param {number} options.width - 패널 너비 (픽셀)
   * @param {number} options.height - 패널 높이 (픽셀)
   * @param {number} options.x - 초기 X 위치
   * @param {number} options.y - 초기 Y 위치
   * @param {string} options.defaultUrl - 기본 URL (선택적)
   * @param {string} options.id - 패널 고유 ID (선택적)
   */
  constructor(options = {}) {
    // 고유 ID 생성 (없는 경우)
    this.id = options.id || this.generateUniqueId();
    
    this.options = {
      title: options.title || '새 패널',
      width: options.width || 400,
      height: options.height || 300,
      x: options.x || 100,
      y: options.y || 100,
      defaultUrl: options.defaultUrl || '',
      url: options.url || '',  // 복원된 URL
      minWidth: options.minWidth || 250,
      minHeight: options.minHeight || 200
    };
    
    this.isMoving = false;
    this.offsetX = 0;
    this.offsetY = 0;
    
    // 크기 조절을 위한 변수
    this.isResizing = false;
    this.resizeDirection = '';
    this.startWidth = 0;
    this.startHeight = 0;
    this.startX = 0;
    this.startY = 0;
    
    // URL 입력창 표시 상태
    this.isUrlInputVisible = false;
    
    this.createPanel();
    this.setupEventListeners();
  }
  
  /**
   * 고유 ID 생성
   * @returns {string} 고유 ID
   */
  generateUniqueId() {
    return 'panel-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
  }
  
  /**
   * 패널 설정 객체 반환
   * @returns {Object} 패널 설정 객체
   */
  getConfig() {
    // 현재 패널의 위치와 크기 가져오기
    const rect = this.panel.getBoundingClientRect();
    
    return {
      id: this.id,
      title: this.titleText.textContent,
      width: rect.width,
      height: rect.height,
      x: rect.left,
      y: rect.top,
      url: this.iframe.src || this.options.defaultUrl,
      minimized: this.panel.classList.contains('panel-minimized'),
      maximized: this.panel.classList.contains('panel-maximized')
    };
  }
  
  /**
   * 패널 DOM 요소 생성
   */
  createPanel() {
    // 패널 컨테이너 생성
    this.panel = document.createElement('div');
    this.panel.className = 'draggable-panel';
    this.panel.dataset.id = this.id; // 패널에 ID 설정
    this.panel.style.width = `${this.options.width}px`;
    this.panel.style.height = `${this.options.height}px`;
    this.panel.style.left = `${this.options.x}px`;
    this.panel.style.top = `${this.options.y}px`;
    
    // 패널에 URL 정보 저장 - 복원된 URL을 우선적으로 사용
    this.panel.dataset.url = this.options.url || this.options.defaultUrl || '';
    
    // 타이틀바 생성
    this.titleBar = document.createElement('div');
    this.titleBar.className = 'panel-titlebar';
    
    // 타이틀 컨테이너 생성 (URL과 원래 제목 모두 표시하기 위함)
    const titleContainer = document.createElement('div');
    titleContainer.className = 'panel-title-container';
    
    // 타이틀 텍스트 (URL 또는 기본 제목)
    this.titleText = document.createElement('div');
    this.titleText.className = 'panel-header-title';
    
    // URL이 제목으로 지정된 경우와 원래 제목이 있는 경우 처리
    if (this.options.url && this.options.title === this.options.url) {
      // URL이 제목인 경우: URL을 제목으로 표시
      this.titleText.textContent = this.options.url;
      this.titleText.title = this.options.url; // 툴크로 전체 URL 표시
      
      // 원래 제목이 있으면 작은 라벨로 표시
      if (this.options.originalTitle) {
        const originalTitleLabel = document.createElement('small');
        originalTitleLabel.className = 'panel-original-title';
        originalTitleLabel.textContent = this.options.originalTitle;
        titleContainer.appendChild(originalTitleLabel);
      }
    } else {
      // 일반적인 경우: 제목을 그대로 표시
      this.titleText.textContent = this.options.title;
    }
    
    titleContainer.appendChild(this.titleText);
    
    // URL 버튼 생성
    this.urlButton = document.createElement('button');
    this.urlButton.className = 'panel-btn panel-url-btn';
    this.urlButton.innerHTML = '<i class="bi bi-globe"></i>';
    this.urlButton.title = 'URL 입력';
    
    // 컨트롤 버튼 컨테이너
    this.controls = document.createElement('div');
    this.controls.className = 'panel-controls';
    
    // 닫기 버튼
    this.closeButton = document.createElement('button');
    this.closeButton.className = 'panel-btn panel-close-btn';
    this.closeButton.innerHTML = '<i class="bi bi-x"></i>';
    this.closeButton.title = '닫기';
    
    // 최소화 버튼
    this.minimizeButton = document.createElement('button');
    this.minimizeButton.className = 'panel-btn panel-minimize-btn';
    this.minimizeButton.innerHTML = '<i class="bi bi-dash"></i>';
    this.minimizeButton.title = '최소화';
    
    // 최대화 버튼 추가
    this.maximizeButton = document.createElement('button');
    this.maximizeButton.className = 'panel-btn panel-maximize-btn';
    this.maximizeButton.innerHTML = '<i class="bi bi-arrows-fullscreen"></i>';
    this.maximizeButton.title = '최대화';
    
    // 새로고침 버튼
    this.refreshButton = document.createElement('button');
    this.refreshButton.className = 'panel-btn panel-refresh-btn';
    this.refreshButton.innerHTML = '<i class="bi bi-arrow-clockwise"></i>';
    this.refreshButton.title = '새로고침';
    
    // 타이틀바 왼쪽 컨트롤 컨테이너
    this.leftControls = document.createElement('div');
    this.leftControls.className = 'panel-left-controls';
    this.leftControls.appendChild(this.urlButton);
    
    // 컨트롤 버튼들을 컨테이너에 추가
    this.controls.appendChild(this.minimizeButton);
    this.controls.appendChild(this.maximizeButton);
    this.controls.appendChild(this.refreshButton);
    this.controls.appendChild(this.closeButton);
    
    // 타이틀바에 요소 추가
    this.titleBar.appendChild(this.leftControls);
    this.titleBar.appendChild(titleContainer);
    this.titleBar.appendChild(this.controls);
    
    // URL 입력 컨테이너
    this.urlContainer = document.createElement('div');
    this.urlContainer.className = 'panel-url-container';
    
    // 기본적으로 URL 입력창 숨김
    this.urlContainer.style.display = 'none';
    
    // URL 입력 필드
    this.urlInput = document.createElement('input');
    this.urlInput.type = 'text';
    this.urlInput.className = 'panel-url-input';
    this.urlInput.placeholder = 'https://example.com';
    this.urlInput.value = this.options.defaultUrl;
    
    // URL 이동 버튼
    this.goButton = document.createElement('button');
    this.goButton.className = 'panel-url-go-btn';
    this.goButton.innerHTML = '<i class="bi bi-arrow-right-circle-fill"></i>';
    this.goButton.title = '이동';
    
    // URL 컨테이너에 요소 추가
    this.urlContainer.appendChild(this.urlInput);
    this.urlContainer.appendChild(this.goButton);
    
    // iframe 컨테이너
    this.contentContainer = document.createElement('div');
    this.contentContainer.className = 'panel-content';
    
    // iframe 생성
    this.iframe = document.createElement('iframe');
    this.iframe.className = 'panel-iframe';
    this.iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms allow-presentation allow-top-navigation allow-downloads');
    this.iframe.setAttribute('loading', 'lazy');
    this.iframe.setAttribute('referrerpolicy', 'no-referrer');
    this.iframe.setAttribute('allow', 'fullscreen; camera; microphone; geolocation');
    
    // iframe 초기화
    if (this.options.defaultUrl) {
      // 기본 URL이 있는 경우 URL 입력창 표시 후 이동
      this.isUrlInputVisible = true;
      this.urlContainer.style.display = 'flex';
      this.iframe.srcdoc = `
        <html>
          <head>
            <style>
              body {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background-color: #f8f9fa;
                font-family: Arial, sans-serif;
                color: #6c757d;
              }
              .loading {
                text-align: center;
              }
              .spinner {
                width: 40px;
                height: 40px;
                margin: 0 auto 15px;
                border: 4px solid rgba(66, 103, 178, 0.2);
                border-top-color: #4267B2;
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            </style>
          </head>
          <body>
            <div class="loading">
              <div class="spinner"></div>
              <p>로딩 중...</p>
            </div>
          </body>
        </html>
      `;
    } else {
      this.iframe.srcdoc = `
        <html>
          <head>
            <style>
              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                background-color: #f8f9fa;
                color: #6c757d;
                text-align: center;
              }
              .container {
                max-width: 80%;
              }
              h3 {
                margin-bottom: 15px;
                color: #4267B2;
              }
              p {
                line-height: 1.5;
              }
              .icon {
                font-size: 3rem;
                margin-bottom: 20px;
                color: #4267B2;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="icon">🌐</div>
              <h3>URL을 입력하세요</h3>
              <p>패널 상단의 지구본 아이콘을 클릭한 후, 주소창에 방문하려는 웹사이트 주소를 입력하고 이동 버튼을 클릭하세요.</p>
              <p><small>예: google.com, naver.com, github.com</small></p>
            </div>
          </body>
        </html>
      `;
    }
    
    // 컨텐츠 컨테이너에 iframe 추가
    this.contentContainer.appendChild(this.iframe);
    
    // 패널에 요소 추가
    this.panel.appendChild(this.titleBar);
    this.panel.appendChild(this.urlContainer);
    this.panel.appendChild(this.contentContainer);
    
    // 크기 조절 핸들 추가
    this.addResizeHandles();
    
    // body에 패널 추가
    document.body.appendChild(this.panel);
    
    // 최소화 상태 적용 (설정에서 복원 시)
    if (this.options.minimized) {
      this.toggleMinimize();
    }
    
    // iframe이 DOM에 추가된 후 URL로 이동 (복원된 패널 또는 기본 URL이 있는 경우)
    if (this.options.url || this.options.defaultUrl) {
      // setTimeout을 사용하여 DOM 업데이트 후 실행되도록 함
      setTimeout(() => {
        // 복원된 URL을 우선으로 사용하거나, 없으면 기본 URL 사용
        const urlToNavigate = this.options.url || this.options.defaultUrl;
        this.navigateToUrl(urlToNavigate);
      }, 0);
    }

    this.panel.draggable = true; // 드래그 가능하도록 설정
  }
  
  /**
   * URL 입력창 토글
   */
  toggleUrlInput() {
    this.isUrlInputVisible = !this.isUrlInputVisible;
    this.urlContainer.style.display = this.isUrlInputVisible ? 'flex' : 'none';
    
    if (this.isUrlInputVisible) {
      // URL 입력창이 표시될 때 포커스 설정
      setTimeout(() => {
        this.urlInput.focus();
        this.urlInput.select();
      }, 100);
    }
  }
  
  /**
   * 크기 조절 핸들 추가
   */
  addResizeHandles() {
    // 방향별 핸들 생성
    const directions = ['n', 'e', 's', 'w', 'ne', 'se', 'sw', 'nw'];
    
    directions.forEach(dir => {
      const handle = document.createElement('div');
      handle.className = `panel-resize-handle ${dir}`;
      handle.setAttribute('data-direction', dir);
      this.panel.appendChild(handle);
      
      // 핸들에 마우스 이벤트 추가
      this.setupResizeHandleEvents(handle, dir);
    });
  }
  
  /**
   * 크기 조절 핸들에 이벤트 리스너 설정
   * @param {HTMLElement} handle - 크기 조절 핸들
   * @param {string} direction - 조절 방향
   */
  setupResizeHandleEvents(handle, direction) {
    // 마우스 다운 이벤트
    handle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // 마우스 우측 버튼(e.button === 2)이면 리사이징 처리 안함
      if (e.button === 2) return;
      
      this.isResizing = true;
      this.resizeDirection = direction;
      
      // 패널 현재 크기와 위치 저장
      const rect = this.panel.getBoundingClientRect();
      this.startWidth = rect.width;
      this.startHeight = rect.height;
      this.startX = rect.left;
      this.startY = rect.top;
      
      // 마우스 시작 위치
      this.mouseStartX = e.clientX;
      this.mouseStartY = e.clientY;
      
      // 리사이즈 중인 상태 표시
      this.panel.classList.add('panel-resizing');
      
      // 패널을 맨 앞으로
      this.bringToFront();
      
      // 마우스 미리보기를 위한 오버레이 추가
      this.addResizeOverlay();
    });
    
    // 터치 시작 이벤트
    handle.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const touch = e.touches[0];
      
      this.isResizing = true;
      this.resizeDirection = direction;
      
      // 패널 현재 크기와 위치 저장
      const rect = this.panel.getBoundingClientRect();
      this.startWidth = rect.width;
      this.startHeight = rect.height;
      this.startX = rect.left;
      this.startY = rect.top;
      
      // 터치 시작 위치
      this.mouseStartX = touch.clientX;
      this.mouseStartY = touch.clientY;
      
      // 리사이즈 중인 상태 표시
      this.panel.classList.add('panel-resizing');
      
      // 패널을 맨 앞으로
      this.bringToFront();
      
      // 터치 미리보기를 위한 오버레이 추가
      this.addResizeOverlay();
    }, { passive: false });
  }
  
  /**
   * 리사이징 미리보기 오버레이 추가
   */
  addResizeOverlay() {
    // 이미 있으면 제거
    this.removeResizeOverlay();
    
    try {
      // 오버레이 생성
      this.resizeOverlay = document.createElement('div');
      this.resizeOverlay.className = 'panel-resize-overlay';
      this.resizeOverlay.style.position = 'fixed';
      this.resizeOverlay.style.top = '0';
      this.resizeOverlay.style.left = '0';
      this.resizeOverlay.style.width = '100%';
      this.resizeOverlay.style.height = '100%';
      this.resizeOverlay.style.zIndex = '9999';
      this.resizeOverlay.style.cursor = this.getCursorStyle(this.resizeDirection);
      
      document.body.appendChild(this.resizeOverlay);
    } catch (error) {
      console.error('리사이즈 오버레이 생성 중 오류 발생:', error);
    }
  }
  
  /**
   * 리사이징 오버레이 제거
   */
  removeResizeOverlay() {
    if (this.resizeOverlay) {
      this.resizeOverlay.remove();
      this.resizeOverlay = null;
    }
  }
  
  /**
   * 리사이징 방향에 따른 커서 스타일 반환
   * @param {string} direction - 조절 방향
   * @returns {string} 커서 스타일
   */
  getCursorStyle(direction) {
    switch (direction) {
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'nw':
      case 'se':
        return 'nwse-resize';
      default:
        return 'move';
    }
  }
  
  /**
   * 이벤트 리스너 설정
   */
  setupEventListeners() {
    // URL 버튼 클릭 이벤트
    this.urlButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleUrlInput();
    });
    
    // 타이틀바 드래그 시작 (마우스)
    this.titleBar.addEventListener('mousedown', (e) => {
      // 컨트롤 버튼 클릭은 드래그로 처리하지 않음
      if (e.target.closest('.panel-btn')) return;
      
      // 마우스 우측 버튼(e.button === 2)이면 드래그 처리 안함
      if (e.button === 2) return;
      
      this.isMoving = true;
      this.panel.classList.add('panel-moving');
      
      // 드래그 시작 위치 계산
      const rect = this.panel.getBoundingClientRect();
      this.offsetX = e.clientX - rect.left;
      this.offsetY = e.clientY - rect.top;
      
      // 패널을 맨 앞으로 가져오기
      this.bringToFront();
    });
    
    // 타이틀바 드래그 시작 (터치)
    this.titleBar.addEventListener('touchstart', (e) => {
      // 컨트롤 버튼 터치는 드래그로 처리하지 않음
      if (e.target.closest('.panel-btn')) return;
      
      this.isMoving = true;
      this.panel.classList.add('panel-moving');
      
      // 드래그 시작 위치 계산
      const rect = this.panel.getBoundingClientRect();
      const touch = e.touches[0];
      this.offsetX = touch.clientX - rect.left;
      this.offsetY = touch.clientY - rect.top;
      
      // 패널을 맨 앞으로 가져오기
      this.bringToFront();
      
      // 스크롤 이벤트 방지
      e.preventDefault();
    }, { passive: false });
    
    // 마우스 이동
    document.addEventListener('mousemove', (e) => {
      if (this.isMoving) {
        // 새 위치 계산
        const x = e.clientX - this.offsetX;
        const y = e.clientY - this.offsetY;
        
        // 패널 위치 업데이트
        this.panel.style.left = `${x}px`;
        this.panel.style.top = `${y}px`;
      } else if (this.isResizing) {
        // 리사이징 처리
        this.handleResize(e.clientX, e.clientY);
      }
    });
    
    // 터치 이동
    document.addEventListener('touchmove', (e) => {
      const touch = e.touches[0];
      
      if (this.isMoving) {
        // 새 위치 계산
        const x = touch.clientX - this.offsetX;
        const y = touch.clientY - this.offsetY;
        
        // 패널 위치 업데이트
        this.panel.style.left = `${x}px`;
        this.panel.style.top = `${y}px`;
        
        // 스크롤 이벤트 방지
        e.preventDefault();
      } else if (this.isResizing) {
        // 리사이징 처리
        this.handleResize(touch.clientX, touch.clientY);
        
        // 스크롤 이벤트 방지
        e.preventDefault();
      }
    }, { passive: false });
    
    // 드래그 종료 (마우스)
    document.addEventListener('mouseup', () => {
      if (this.isMoving) {
        this.endDrag();
      }
      if (this.isResizing) {
        this.endResize();
      }
    });
    
    // 드래그 종료 (터치)
    document.addEventListener('touchend', () => {
      if (this.isMoving) {
        this.endDrag();
      }
      if (this.isResizing) {
        this.endResize();
      }
    });
    
    // 마우스가 창 밖으로 나갔을 때도 드래그 종료
    document.addEventListener('mouseleave', () => {
      if (this.isMoving) {
        this.endDrag();
      }
      if (this.isResizing) {
        this.endResize();
      }
    });
    
    // 닫기 버튼 클릭
    this.closeButton.addEventListener('click', () => {
      // 패널 삭제 전 설정에서도 제거
      if (panelManager) {
        panelManager.removePanel(this.id);
      }
      this.close();
    });
    
    // 최소화 버튼 클릭
    this.minimizeButton.addEventListener('click', () => {
      this.toggleMinimize();
    });
    
    // 최대화 버튼 클릭
    this.maximizeButton.addEventListener('click', () => {
      this.toggleMaximize();
    });
    
    // 새로고침 버튼 클릭
    this.refreshButton.addEventListener('click', () => {
      this.refreshContent();
    });
    
    // URL 입력 필드에서 Enter 키
    this.urlInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this.navigateToUrl(this.urlInput.value);
      }
    });
    
    // 이동 버튼 클릭
    this.goButton.addEventListener('click', () => {
      this.navigateToUrl(this.urlInput.value);
    });
    
    // 창 크기가 변경되면 패널이 화면 밖으로 나가지 않도록 위치 조정
    window.addEventListener('resize', () => {
      this.adjustPositionToViewport();
    });
    
    // ESC 키를 눌렀을 때 URL 입력창 숨기기 및 드래그/리사이징 중단
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        // URL 입력창이 보이는 상태면 숨김
        if (this.isUrlInputVisible) {
          this.toggleUrlInput();
        }
        
        // 드래그 중이면 중단
        if (this.isMoving) {
          this.endDrag();
        }
        
        // 리사이징 중이면 중단
        if (this.isResizing) {
          this.endResize();
        }
      }
    });
    
    // 마우스 우측 클릭 시 드래그/리사이징 중단
    document.addEventListener('contextmenu', (e) => {
      // 드래그 중이거나 리사이징 중이면 컨텍스트 메뉴 방지
      if (this.isMoving || this.isResizing) {
        e.preventDefault();
        
        // 드래그 중이면 중단
        if (this.isMoving) {
          this.endDrag();
        }
        
        // 리사이징 중이면 중단
        if (this.isResizing) {
          this.endResize();
        }
      }
    });
    
    // 패널 설정 변경 시 저장 기능 추가
    const saveConfig = () => {
      // 패널 설정 저장 (PanelManager를 통해)
      if (panelManager) {
        panelManager.saveConfig();
      }
    };
    
    // 드래그 종료 후 설정 저장
    const originalEndDrag = this.endDrag.bind(this);
    this.endDrag = () => {
      if (this.isMoving) {
        originalEndDrag();
        saveConfig();
      }
    };
    
    // 리사이징 종료 후 설정 저장
    const originalEndResize = this.endResize.bind(this);
    this.endResize = () => {
      if (this.isResizing) {
        originalEndResize();
        saveConfig();
      }
    };
    
    // URL 변경 시 설정 저장
    const originalNavigateToUrl = this.navigateToUrl.bind(this);
    this.navigateToUrl = (url) => {
      originalNavigateToUrl(url);
      saveConfig();
    };

    // HTML Drag and Drop API 이벤트 리스너 추가
    this.panel.addEventListener('dragstart', this.handleDragStart.bind(this));
    this.panel.addEventListener('dragend', this.handleDragEnd.bind(this));
  }
  
  /**
   * 패널 드래그 시작 핸들러 (HTML Drag and Drop API)
   */
  handleDragStart(event) {
    // 컨트롤 버튼 클릭 시 드래그 시작 안 함
    if (event.target.closest('.panel-btn')) {
        event.preventDefault();
        return;
    }
    console.debug('패널 드래그 시작 (API):', this.id);
    event.dataTransfer.setData('text/plain', this.id); // 패널 ID 전달
    event.dataTransfer.effectAllowed = 'move';
    // 드래그 중인 패널 표시
    event.currentTarget.classList.add('dragging');
    // 드래그 이미지 설정 (선택 사항)
    // event.dataTransfer.setDragImage(this.panel, 10, 10);
  }

  /**
   * 패널 드래그 종료 핸들러 (HTML Drag and Drop API)
   */
  handleDragEnd(event) {
    console.debug('패널 드래그 종료 (API):', this.id);
    event.currentTarget.classList.remove('dragging');
    // 여기서 endDrag()를 호출할 필요는 없음. API 드래그 종료 시 필요한 정리만 수행
  }
  
  /**
   * 리사이징 처리
   * @param {number} mouseX - 마우스 X 위치
   * @param {number} mouseY - 마우스 Y 위치
   */
  handleResize(mouseX, mouseY) {
    const deltaX = mouseX - this.mouseStartX;
    const deltaY = mouseY - this.mouseStartY;
    
    let newWidth = this.startWidth;
    let newHeight = this.startHeight;
    let newLeft = this.startX;
    let newTop = this.startY;
    
    // 방향에 따른 크기 및 위치 변경
    // 북쪽(위)
    if (this.resizeDirection.includes('n')) {
      newHeight = Math.max(this.options.minHeight, this.startHeight - deltaY);
      newTop = this.startY + (this.startHeight - newHeight);
    }
    
    // 남쪽(아래)
    if (this.resizeDirection.includes('s')) {
      newHeight = Math.max(this.options.minHeight, this.startHeight + deltaY);
    }
    
    // 동쪽(오른쪽)
    if (this.resizeDirection.includes('e')) {
      newWidth = Math.max(this.options.minWidth, this.startWidth + deltaX);
    }
    
    // 서쪽(왼쪽)
    if (this.resizeDirection.includes('w')) {
      newWidth = Math.max(this.options.minWidth, this.startWidth - deltaX);
      newLeft = this.startX + (this.startWidth - newWidth);
    }
    
    // 패널 크기 및 위치 업데이트
    this.panel.style.width = `${newWidth}px`;
    this.panel.style.height = `${newHeight}px`;
    this.panel.style.left = `${newLeft}px`;
    this.panel.style.top = `${newTop}px`;
    
    // 최소화된 상태에서 높이 조정하면 최소화 해제
    if (this.panel.classList.contains('panel-minimized') && this.resizeDirection.includes('s')) {
      this.panel.classList.remove('panel-minimized');
      this.contentContainer.style.display = 'block';
      this.urlContainer.style.display = 'flex';
      this.minimizeButton.innerHTML = '<i class="bi bi-dash"></i>';
      
      // 새 높이 적용
      this.options.height = newHeight;
    }
  }
  
  /**
   * 리사이징 종료
   */
  endResize() {
    if (this.isResizing) {
      this.isResizing = false;
      this.panel.classList.remove('panel-resizing');
      
      // 새 크기 저장
      const rect = this.panel.getBoundingClientRect();
      this.options.width = rect.width;
      this.options.height = rect.height;
      
      // 리사이징 후 패널이 화면 밖으로 나가지 않도록 위치 조정
      this.adjustPositionToViewport();
      
      // 오버레이 제거
      this.removeResizeOverlay();
    }
  }
  
  /**
   * 드래그 종료 처리
   */
  endDrag() {
    if (this.isMoving) {
      this.isMoving = false;
      this.panel.classList.remove('panel-moving');
      
      // 드래그 종료 후 패널이 화면 밖으로 나가지 않도록 위치 조정
      this.adjustPositionToViewport();
    }
  }
  
  /**
   * 패널이 화면 밖으로 나가지 않도록 위치 조정
   */
  adjustPositionToViewport() {
    // 현재 패널 위치와 크기
    const rect = this.panel.getBoundingClientRect();
    
    // 화면 크기
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let positionChanged = false;
    
    // 패널이 화면 오른쪽을 벗어난 경우
    if (rect.right > viewportWidth) {
      const newLeft = viewportWidth - rect.width;
      this.panel.style.left = `${Math.max(0, newLeft)}px`;
      positionChanged = true;
    }
    
    // 패널이 화면 아래쪽을 벗어난 경우
    if (rect.bottom > viewportHeight) {
      const newTop = viewportHeight - rect.height;
      this.panel.style.top = `${Math.max(0, newTop)}px`;
      positionChanged = true;
    }
    
    // 패널이 화면 왼쪽을 벗어난 경우
    if (rect.left < 0) {
      this.panel.style.left = '0px';
      positionChanged = true;
    }
    
    // 패널이 화면 위쪽을 벗어난 경우
    if (rect.top < 0) {
      this.panel.style.top = '0px';
      positionChanged = true;
    }
    
    // 최소화된 패널은 항상 타이틀바가 보이도록
    if (this.panel.classList.contains('panel-minimized') && rect.top < 0) {
      const titleBarHeight = this.titleBar.offsetHeight;
      this.panel.style.top = `${-rect.height + titleBarHeight + 10}px`;
      positionChanged = true;
    }
    
    // 위치가 변경된 경우 설정 저장
    if (positionChanged && panelManager) {
      // 약간의 지연을 두어 자주 저장하지 않도록 함
      setTimeout(() => panelManager.saveConfig(), 500);
    }
  }
  
  /**
   * URL로 iframe 이동
   * @param {string} url - 이동할 URL
   */
  navigateToUrl(url) {
    if (!url) return;
    
    try {
      // URL 형식 확인 및 수정
      let formattedUrl = url.trim();
      if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
        formattedUrl = 'https://' + formattedUrl;
      }
      
      // URL 유효성 검사
      new URL(formattedUrl);
      
      // 로딩 중 표시
      this.contentContainer.classList.add('loading');
      
      // iframe 엘리먼트 재생성 (캐시 및 이전 상태 문제 방지)
      const newIframe = document.createElement('iframe');
      newIframe.className = 'panel-iframe';
      newIframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms allow-presentation allow-top-navigation allow-downloads');
      newIframe.setAttribute('loading', 'lazy');
      newIframe.setAttribute('referrerpolicy', 'no-referrer');
      newIframe.setAttribute('allow', 'fullscreen; camera; microphone; geolocation');
      
      // 로드 이벤트 설정
      newIframe.onload = () => {
        this.contentContainer.classList.remove('loading');
        
        // 페이지 로드 후 URL 입력창 자동 숨김
        if (this.isUrlInputVisible) {
          this.toggleUrlInput();
        }
        
        // 로드 후 패널 제목 업데이트 시도
        try {
          // iframe에서 title 가져오기 시도
          let newTitle = '';
          try {
            newTitle = newIframe.contentDocument?.title;
          } catch (e) {
            // CORS 정책으로 접근 불가능한 경우
          }
          
          // 제목이 없거나 접근할 수 없는 경우 URL의 호스트명 사용
          if (!newTitle) {
            const urlObj = new URL(formattedUrl);
            newTitle = urlObj.hostname;
          }
          
          // 제목 업데이트
          if (newTitle) {
            this.titleText.textContent = newTitle;
            
            // 설정 변경 저장을 위해 PanelManager에 알림
            if (panelManager) {
              panelManager.saveConfig();
            }
          }
        } catch (e) {
          // 제목 업데이트 실패 시 무시
          console.error('Failed to update panel title:', e);
        }
      };
      
      // 로딩 에러 처리
      newIframe.onerror = () => {
        this.contentContainer.classList.remove('loading');
        this.showErrorPage(formattedUrl);
      };
      
      // iframe에 URL 설정
      newIframe.src = formattedUrl;
      
      // this.iframe이 contentContainer의 자식인지 확인하고 교체 또는 추가
      try {
        if (this.contentContainer.contains(this.iframe)) {
          // 자식 노드라면 교체
          this.contentContainer.replaceChild(newIframe, this.iframe);
        } else {
          // 자식 노드가 아니라면 기존 내용 비우고 새로 추가
          this.contentContainer.innerHTML = '';
          this.contentContainer.appendChild(newIframe);
        }
        // 참조 업데이트
        this.iframe = newIframe;
      } catch (error) {
        console.error('iframe 교체 중 오류 발생:', error);
        // 오류 발생 시 안전하게 추가
        this.contentContainer.innerHTML = '';
        this.contentContainer.appendChild(newIframe);
        this.iframe = newIframe;
      }
      
      // 입력 필드 업데이트
      this.urlInput.value = formattedUrl;
      
      // 15초 후에도 로딩 중이면 타임아웃으로 간주
      setTimeout(() => {
        if (this.contentContainer.classList.contains('loading')) {
          this.contentContainer.classList.remove('loading');
          
          // URL 입력창 자동 숨김
          if (this.isUrlInputVisible) {
            this.toggleUrlInput();
          }
          
          // iframe이 여전히 로드되지 않았는지 확인 (보안 정책으로 인해 접근할 수 없을 수 있음)
          try {
            if (!this.iframe.contentDocument) {
              // 타임아웃 메시지 대신 계속 로드 중으로 표시 (X-Frame-Options 또는 CORS 정책으로 인한 차단일 수 있음)
            }
          } catch (e) {
            // 보안 정책으로 인해 contentDocument에 접근하지 못할 경우 무시
          }
        }
      }, 15000);
      
      // URL이 변경되었으므로 설정 저장
      if (panelManager) {
        // 약간의 지연을 두고 저장 (너무 자주 저장하지 않도록)
        setTimeout(() => panelManager.saveConfig(), 300);
      }
      
    } catch (error) {
      console.error('잘못된 URL:', error);
      this.contentContainer.classList.remove('loading');
      
      // 알림 대신 패널 내에 오류 표시
      this.showErrorPage(url, error.message);
    }
  }
  
  /**
   * iframe 내용 새로고침
   */
  refreshContent() {
    if (this.iframe.src) {
      this.iframe.src = this.iframe.src;
    }
  }
  
  /**
   * 패널 닫기
   * @param {boolean} fromPanelManager - PanelManager에서 호출되었는지 여부
   */
  close(fromPanelManager = false) {
    // 패널 제거 전 설정에서도 제거
    if (panelManager && !fromPanelManager) {
      panelManager.removePanel(this.id);
    }
    this.panel.remove();
  }
  
  /**
   * 패널 최소화/복원 토글
   */
  toggleMinimize() {
    if (this.panel.classList.contains('panel-minimized')) {
      // 복원
      this.panel.classList.remove('panel-minimized');
      this.contentContainer.style.display = 'block';
      this.urlContainer.style.display = 'flex';
      this.minimizeButton.innerHTML = '<i class="bi bi-dash"></i>';
      this.panel.style.height = `${this.options.height}px`;
    } else {
      // 최소화
      this.panel.classList.add('panel-minimized');
      this.contentContainer.style.display = 'none';
      this.urlContainer.style.display = 'none';
      this.minimizeButton.innerHTML = '<i class="bi bi-chevron-expand"></i>';
      this.panel.style.height = 'auto';
    }
  }
  
  /**
   * 패널 최대화/원래 크기로 토글
   */
  toggleMaximize() {
    // 최대화 상태 확인
    const isMaximized = this.panel.classList.contains('panel-maximized');
    
    if (isMaximized) {
      // 원래 크기로 복원
      this.panel.classList.remove('panel-maximized');
      
      // 원래 위치와 크기로 복원
      this.panel.style.width = `${this.options.width}px`;
      this.panel.style.height = `${this.options.height}px`;
      this.panel.style.left = `${this.options.x}px`;
      this.panel.style.top = `${this.options.y}px`;
      
      // 아이콘 변경
      this.maximizeButton.innerHTML = '<i class="bi bi-arrows-fullscreen"></i>';
      this.maximizeButton.title = '최대화';
    } else {
      // 현재 위치와 크기 저장
      const rect = this.panel.getBoundingClientRect();
      this.options.width = rect.width;
      this.options.height = rect.height;
      this.options.x = rect.left;
      this.options.y = rect.top;
      
      // 패널 최대화
      this.panel.classList.add('panel-maximized');
      
      // 전체 화면 크기로 설정 (여백 없이 화면 전체 채우기)
      this.panel.style.width = `${window.innerWidth}px`;
      this.panel.style.height = `${window.innerHeight}px`;
      this.panel.style.left = '0px';
      this.panel.style.top = '0px';
      
      // 아이콘 변경
      this.maximizeButton.innerHTML = '<i class="bi bi-arrows-angle-contract"></i>';
      this.maximizeButton.title = '원래 크기로';
    }
    
    // 패널 설정 저장 (PanelManager를 통해)
    if (typeof saveConfig === 'function') {
      saveConfig();
    }
  }
  
  /**
   * 패널을 맨 앞으로 가져오기
   */
  bringToFront() {
    // 현재 가장 높은 z-index 찾기
    const panels = document.querySelectorAll('.draggable-panel');
    let maxZIndex = 0;
    
    panels.forEach(panel => {
      const zIndex = parseInt(window.getComputedStyle(panel).zIndex) || 0;
      maxZIndex = Math.max(maxZIndex, zIndex);
    });
    
    // 현재 패널의 z-index를 최댓값 + 1로 설정
    this.panel.style.zIndex = maxZIndex + 1;
  }
  
  /**
   * 오류 페이지 표시
   * @param {string} url - 시도한 URL
   * @param {string} errorMsg - 오류 메시지 (선택적)
   */
  showErrorPage(url, errorMsg) {
    this.iframe.srcdoc = `
      <html>
        <head>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
              margin: 0;
              background-color: #fff9f9;
              color: #6c757d;
              text-align: center;
            }
            .container {
              max-width: 80%;
            }
            h3 {
              margin-bottom: 15px;
              color: #e74c3c;
            }
            .url {
              font-family: monospace;
              background-color: #f8f9fa;
              padding: 5px 10px;
              border-radius: 4px;
              word-break: break-all;
            }
            p {
              line-height: 1.5;
              margin-bottom: 15px;
            }
            .icon {
              font-size: 3rem;
              margin-bottom: 20px;
              color: #e74c3c;
            }
            .error-msg {
              background-color: #f8d7da;
              border-radius: 4px;
              padding: 10px;
              margin-top: 15px;
              color: #721c24;
              font-size: 0.9rem;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="icon">⚠️</div>
            <h3>페이지를 로드할 수 없습니다</h3>
            <p>다음 URL에 연결할 수 없습니다:</p>
            <p class="url">${url}</p>
            <p>올바른 URL을 입력했는지 확인하세요.</p>
            ${errorMsg ? `<div class="error-msg">${errorMsg}</div>` : ''}
            <p>일부 웹사이트는 보안 정책(X-Frame-Options 또는 CORS)으로 인해 iframe 내에서 열리지 않을 수 있습니다.</p>
          </div>
        </body>
      </html>
    `;
  }

  /**
   * 게시물 내부에 삽입하기 위한 미리보기 HTML 생성
   * @returns {string} 삽입용 HTML 문자열
   */
  toHTMLEmbed() {
    const config = this.getConfig();
    const container = document.createElement('div');
    container.classList.add('embedded-panel-preview');
    
    // 패널 데이터 속성 추가
    container.setAttribute('data-panel-url', config.url || '');
    container.setAttribute('data-panel-title', config.title || '웹 패널');
    container.setAttribute('data-panel-width', config.width || 400);
    container.setAttribute('data-panel-height', config.height || 300);
    
    // 헤더 섹션 생성
    const header = document.createElement('div');
    header.classList.add('embedded-panel-header');
    
    // 제목과 링크
    const titleLink = document.createElement('a');
    titleLink.href = config.url || '#';
    titleLink.target = '_blank';
    titleLink.classList.add('embedded-panel-title');
    
    const icon = document.createElement('i');
    icon.className = 'bi bi-link-45deg me-2';
    titleLink.appendChild(icon);
    
    const titleText = document.createElement('span');
    titleText.textContent = config.title || '웹 패널';
    titleLink.appendChild(titleText);
    
    // URL 표시 (도메인만)
    if (config.url) {
      try {
        const urlSmall = document.createElement('small');
        urlSmall.className = 'ms-2 text-muted';
        urlSmall.textContent = `(${new URL(config.url).hostname})`;
        titleLink.appendChild(urlSmall);
      } catch (e) {
        console.warn('임베드용 URL 파싱 실패:', config.url);
      }
    }
    
    header.appendChild(titleLink);
    
    // 복원 버튼 추가
    const restoreButton = document.createElement('button');
    restoreButton.className = 'btn btn-sm btn-outline-secondary restore-panel-btn float-end';
    restoreButton.title = '패널로 복원';
    
    const restoreIcon = document.createElement('i');
    restoreIcon.className = 'bi bi-box-arrow-up-right';
    restoreButton.appendChild(restoreIcon);
    
    header.appendChild(restoreButton);
    container.appendChild(header);
    
    // iframe으로 실제 콘텐츠 표시
    if (config.url) {
      // 콘텐츠 섹션 생성
      const content = document.createElement('div');
      content.classList.add('embedded-panel-content');
      
      // iframe 생성
      const iframe = document.createElement('iframe');
      iframe.src = config.url;
      iframe.loading = 'lazy';
      iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups allow-forms');
      iframe.setAttribute('referrerpolicy', 'no-referrer');
      
      content.appendChild(iframe);
      container.appendChild(content);
    }
    
    return container.outerHTML;
  }

  /**
   * 패널 위치 설정
   * @param {number} x - X 좌표
   * @param {number} y - Y 좌표
   */
  setPosition(x, y) {
    if (x < 0) x = 0;
    if (y < 0) y = 0;
    
    // 화면 밖으로 나가지 않도록 보정
    if (x + this.panel.offsetWidth > window.innerWidth) {
      x = window.innerWidth - this.panel.offsetWidth;
    }
    
    if (y + this.panel.offsetHeight > window.innerHeight) {
      y = window.innerHeight - this.panel.offsetHeight;
    }
    
    // 위치 업데이트
    this.panel.style.left = `${x}px`;
    this.panel.style.top = `${y}px`;
    
    // 옵션 값도 업데이트
    this.options.x = x;
    this.options.y = y;
  }
  
  /**
   * 패널 너비 설정
   * @param {number} width - 너비(px)
   */
  setWidth(width) {
    if (width < this.options.minWidth) {
      width = this.options.minWidth;
    }
    
    this.panel.style.width = `${width}px`;
    this.options.width = width;
  }
  
  /**
   * 패널 높이 설정
   * @param {number} height - 높이(px)
   */
  setHeight(height) {
    if (height < this.options.minHeight) {
      height = this.options.minHeight;
    }
    
    this.panel.style.height = `${height}px`;
    this.options.height = height;
  }
  
  /**
   * 패널 제목 설정
   * @param {string} title - 패널 제목
   */
  setTitle(title) {
    this.titleText.textContent = title || '새 패널';
    this.options.title = title;
  }
  
  /**
   * 패널 URL 설정
   * @param {string} url - 패널 URL
   */
  setURL(url) {
    if (url && url !== this.iframe.src) {
      this.navigateToUrl(url);
      this.panel.dataset.url = url;
    }
  }
}

/**
 * 패널 생성 함수
 * @param {Object} options - 패널 설정 옵션
 * @returns {DraggablePanel} 생성된 드래그 가능 패널 인스턴스
 */
export function createDraggablePanel(options = {}) {
  return new DraggablePanel(options);
}

/**
 * 패널 그룹 관리를 위한 클래스
 */
export class PanelManager {
  constructor() {
    this.panels = {};
    this.container = null;
    this.maxPanelId = 0;
    this.initialized = false;
    
    this.isFullscreen = false;
    this.fullscreenPanel = null;
    
    // ESC 키 이벤트를 추가해 전체 화면 모드 종료
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isFullscreen) {
        this.exitFullscreen();
      }
    });
  }

  init(containerSelector = '#panels-container') {
    console.debug('PanelManager init');
    
    if (this.initialized) {
      console.log('Panel manager already initialized');
      return;
    }

    this.container = document.querySelector(containerSelector);
    if (!this.container) {
      console.error(`Container not found: ${containerSelector}`);
      return;
    }

    // Make the panel manager accessible globally
    window.panelManager = this;
    
    console.log('Panel manager initialized');
    this.initialized = true;
    
    // Load saved panels when initialized
    this.loadConfig();
    
    // Add event listener for embedding panels
    document.addEventListener('click', this.handleCreatePanelClick.bind(this));
    document.addEventListener('click', this.handleEmbedPanelClick.bind(this));
    document.addEventListener('click', this.handleRestorePanelClick.bind(this));
  }
  
  handleCreatePanelClick(event) {
    event.preventDefault();
    
    // Find associated post element
    const postElement = event.target.closest('.post-card');
    if (!postElement) {
      console.error('Post element not found');
      return;
    }
    
    // Make sure we have a panel being dragged
    if (!window.currentDraggingPanel) {
      window.showToast('Please select a panel first by clicking on its title bar', 'warning');
      return;
    }
    
    // Delegate to the post component's handler if available
    if (typeof handleCreatePanel === 'function') {
      handleCreatePanel(event);
    } else {
      console.error('handleCreatePanel function not found');
    }
  }

  handleEmbedPanelClick(event) {
    event.preventDefault();
    
    // Find associated post element
    const postElement = event.target.closest('.post-card');
    if (!postElement) {
      console.error('Post element not found');
      return;
    }
    
    // Make sure we have a panel being dragged
    if (!window.currentDraggingPanel) {
      window.showToast('Please select a panel first by clicking on its title bar', 'warning');
      return;
    }
    
    // Delegate to the post component's handler if available
    if (typeof handleEmbedPanel === 'function') {
      handleEmbedPanel(event);
    } else {
      console.error('handleEmbedPanel function not found');
    }
  }

  handleRestorePanelClick(event) {
    const restoreBtn = event.target.closest('.restore-panel-btn');
    if (!restoreBtn) return;
    
    // Find the embedded panel container
    const embeddedPanel = restoreBtn.closest('.embedded-panel-preview');
    if (!embeddedPanel) return;
    
    // Extract panel data from data attributes
    const panelData = {
      url: embeddedPanel.getAttribute('data-panel-url'),
      title: embeddedPanel.getAttribute('data-panel-title'),
      width: parseInt(embeddedPanel.getAttribute('data-panel-width'), 10),
      height: parseInt(embeddedPanel.getAttribute('data-panel-height'), 10)
    };
    
    // Restore the panel
    const panel = this.restorePanel(panelData);
    
    // If panel was successfully restored, remove the embedded version
    if (panel && embeddedPanel.parentElement) {
      embeddedPanel.parentElement.removeChild(embeddedPanel);
    }
  }

  /**
   * ID로 패널 인스턴스를 반환하는 메서드 추가
   * @param {string} id - 찾을 패널의 ID
   * @returns {DraggablePanel|null} 패널 인스턴스 또는 null
   */
  getPanelById(id) {
    return this.panels[id] || null;
  }

  /**
   * 새 패널 생성
   * @param {Object} options - 패널 설정 옵션
   * @returns {DraggablePanel} 생성된 패널 인스턴스
   */
  createPanel(options = {}) {
    // 고유 ID 보장
    if (!options.id || this.panels[options.id]) {
      options.id = this.generateUniqueId();
    }
    
    const panel = new DraggablePanel(options);
    this.panels[options.id] = panel;
    // panelManager 인스턴스를 DraggablePanel 내부에서 접근할 수 있도록 설정
    panel.panelManager = this;
    
    console.log(`패널 생성됨: ID=${panel.id}, 제목=${panel.options.title}`);
    this.saveConfig(); // 새 패널 생성 후 설정 저장
    return panel;
  }

  removePanel(id) {
    const panel = this.panels[id];
    if (panel) {
      panel.close(true); // 패널 DOM 요소 제거 (fromPanelManager = true)
      delete this.panels[id];
      console.log(`패널 제거됨: ID=${id}`);
      this.saveConfig();
    }
  }

  closeAllPanels() {
    if (Object.keys(this.panels).length === 0) {
      console.log('No panels to close');
      return;
    }

    Object.keys(this.panels).forEach(panelId => {
      this.removePanel(panelId);
    });
    
    console.log('All panels closed');
    this.saveConfig();
  }

  saveConfig() {
    if (Object.keys(this.panels).length === 0) {
        // 패널이 하나도 없으면 기존 설정 제거
        try {
            localStorage.removeItem(this.configKey);
            console.log('패널 설정이 비어있어 로컬 스토리지에서 제거되었습니다.');
        } catch (error) {
            console.warn('로컬 스토리지 접근 중 오류 발생 (설정 제거 실패):', error);
        }
        return; // 저장할 내용 없음
    }

    const config = Object.values(this.panels).map(panel => panel.getConfig());
    try {
      localStorage.setItem(this.configKey, JSON.stringify(config));
      console.log('패널 설정이 로컬 스토리지에 저장되었습니다.', config);
    } catch (error) {
      console.error('로컬 스토리지 저장 실패:', error);
      // 메모리 캐시 등에 저장하는 대체 로직 추가 가능
    }
  }

  loadConfig() {
    try {
      const configStr = localStorage.getItem(this.configKey) || '[]';
      const config = JSON.parse(configStr);
      
      // Clear any existing panels
      Object.keys(this.panels).forEach(id => {
        const panel = this.getPanelById(id);
        if (panel && panel.element) {
          panel.element.remove();
        }
      });
      this.panels = {};
      
      if (Array.isArray(config) && config.length > 0) {
        config.forEach(panelConfig => {
          this.createPanel(panelConfig);
        });
        console.log(`Loaded ${config.length} panels from config`);
      } else {
        console.log('No saved panels found');
      }
    } catch (e) {
      console.error('Failed to load panel config:', e);
    }
  }

  // Restore a panel that was embedded in a post
  restorePanel(panelData) {
    if (!panelData || !panelData.url) {
      console.error('Invalid panel data for restoration');
      return null;
    }
    
    // Create new panel with the saved data
    const panel = this.createPanel({
      url: panelData.url,  // This passes the URL to the panel options
      title: panelData.title || '웹 패널',
      width: panelData.width || 400,
      height: panelData.height || 300,
      // Position the panel in the center of the screen
      x: (window.innerWidth - (panelData.width || 400)) / 2,  // Changed from left to x
      y: (window.innerHeight - (panelData.height || 300)) / 2  // Changed from top to y
    });
    
    if (panel) {
      // Bring the restored panel to front
      panel.bringToFront();
      
      // Show a success toast
      if (window.showToast) {
        window.showToast('패널이 성공적으로 복원되었습니다', 'success');
      }
    }
    
    return panel;
  }

  exportConfigToJson() {
    if (Object.keys(this.panels).length === 0) {
      alert('내보낼 패널 설정이 없습니다.');
      return;
    }
    
    const config = Object.values(this.panels).map(panel => panel.getConfig());
    const jsonString = JSON.stringify(config, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sns-panel-config.json';
    document.body.appendChild(a);
    a.click();
    
    // 리소스 정리
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log('패널 설정을 JSON 파일로 내보냈습니다.');
  }

  importConfigFromJson(file) {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('파일이 선택되지 않았습니다.'));
        return;
      }
      
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonString = event.target.result;
          const configs = JSON.parse(jsonString);
          
          // 유효성 검사 (배열인지, 필요한 속성이 있는지 등)
          if (!Array.isArray(configs)) {
            throw new Error('잘못된 설정 파일 형식입니다 (배열이 아님).');
          }
          
          if (configs.length > 0) {
            const firstConfig = configs[0];
            if (typeof firstConfig.id === 'undefined' || 
                typeof firstConfig.x === 'undefined' || 
                typeof firstConfig.y === 'undefined' || 
                typeof firstConfig.width === 'undefined' || 
                typeof firstConfig.height === 'undefined') {
              throw new Error('설정 파일에 필수 속성(id, x, y, width, height)이 없습니다.');
            }
          }
          
          console.log('JSON 파일에서 설정 가져오기 성공:', configs);
          
          // 기존 패널 모두 닫기
          this.closeAllPanels();
          
          // 새 설정으로 패널 생성
          configs.forEach(config => {
            this.createPanel(config);
          });
          
          // 성공적으로 가져왔음을 알림
          resolve('패널 설정을 성공적으로 가져왔습니다.');
          
        } catch (error) {
          console.error('설정 파일 처리 중 오류:', error);
          reject(new Error('설정 파일 처리 중 오류가 발생했습니다: ' + error.message));
        }
      };
      
      reader.onerror = (error) => {
        console.error('파일 읽기 오류:', error);
        reject(new Error('파일을 읽는 중 오류가 발생했습니다.'));
      };
      
      reader.readAsText(file);
    });
  }

  generateUniqueId() {
    return 'panel-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
  }

  destroy() {
    this.closeAllPanels();
    // 이벤트 리스너 제거 등 추가 정리 작업
    window.removeEventListener('beforeunload', this.saveConfig);
    console.log('PanelManager가 제거되었습니다.');
  }

  /**
   * 전체화면 토글
   * @param {HTMLElement} panel 패널 요소
   */
  toggleFullscreen(panel) {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen(panel);
    }
  }
  
  /**
   * 전체화면 모드 진입
   * @param {HTMLElement} panel 패널 요소
   */
  enterFullscreen(panel) {
    if (this.isFullscreen) return;
    
    this.isFullscreen = true;
    this.fullscreenPanel = panel;
    
    // 원래 위치와 크기 저장
    panel.dataset.originalLeft = panel.style.left;
    panel.dataset.originalTop = panel.style.top;
    panel.dataset.originalWidth = panel.style.width;
    panel.dataset.originalHeight = panel.style.height;
    panel.dataset.originalZIndex = panel.style.zIndex;
    
    // 전체화면 스타일 적용
    panel.classList.add('fullscreen-panel');
    panel.style.left = '0';
    panel.style.top = '0';
    panel.style.width = '100%';
    panel.style.height = '100%';
    panel.style.zIndex = '10000';
    
    // 버튼 아이콘 변경
    const fullscreenBtn = panel.querySelector('.panel-fullscreen-btn i');
    if (fullscreenBtn) {
      fullscreenBtn.className = 'fas fa-compress';
    }
    
    // 다른 패널 숨기기
    document.querySelectorAll('.draggable-panel').forEach(p => {
      if (p !== panel) {
        p.style.display = 'none';
      }
    });
    
    // 모달 배경 추가
    const overlay = document.createElement('div');
    overlay.id = 'fullscreen-overlay';
    overlay.className = 'fullscreen-overlay';
    document.body.appendChild(overlay);
    
    // 드래그 비활성화
    panel.querySelector('.panel-header').style.cursor = 'default';
    this.makePanelUndraggable(panel);
    
    // 토스트 메시지 표시
    showToast('전체화면 모드로 전환되었습니다. ESC 키를 누르면 종료됩니다.');
  }
  
  /**
   * 전체화면 모드 종료
   */
  exitFullscreen() {
    if (!this.isFullscreen || !this.fullscreenPanel) return;
    
    const panel = this.fullscreenPanel;
    
    // 원래 스타일로 복원
    panel.classList.remove('fullscreen-panel');
    panel.style.left = panel.dataset.originalLeft;
    panel.style.top = panel.dataset.originalTop;
    panel.style.width = panel.dataset.originalWidth;
    panel.style.height = panel.dataset.originalHeight;
    panel.style.zIndex = panel.dataset.originalZIndex;
    
    // 버튼 아이콘 변경
    const fullscreenBtn = panel.querySelector('.panel-fullscreen-btn i');
    if (fullscreenBtn) {
      fullscreenBtn.className = 'fas fa-expand';
    }
    
    // 다른 패널 다시 표시
    document.querySelectorAll('.draggable-panel').forEach(p => {
      p.style.display = '';
    });
    
    // 오버레이 제거
    const overlay = document.getElementById('fullscreen-overlay');
    if (overlay) {
      overlay.remove();
    }
    
    // 드래그 다시 활성화
    panel.querySelector('.panel-header').style.cursor = 'move';
    this.makePanelDraggable(panel);
    
    this.isFullscreen = false;
    this.fullscreenPanel = null;
    
    // 토스트 메시지 표시
    showToast('전체화면 모드가 종료되었습니다.');
  }
  
  /**
   * 패널 드래그 비활성화
   * @param {HTMLElement} panel 패널 요소
   */
  makePanelUndraggable(panel) {
    const header = panel.querySelector('.panel-header');
    if (header) {
      header.removeEventListener('mousedown', this.panelMouseDownHandler);
      header.removeEventListener('touchstart', this.panelTouchStartHandler);
    }
  }

  /**
   * 임베디드 패널에서 복원
   * @param {Object} panel 패널 데이터
   */
  createPanelFromEmbedded(panel) {
    // 기존 함수 호출로 통일
    return this.createPanel(panel);
  }

  /**
   * 패널 속성 업데이트
   * @param {string} id - 패널 ID
   * @param {Object} props - 업데이트할 속성
   * @returns {boolean} 업데이트 성공 여부
   */
  updatePanelProps(id, props) {
    const panel = this.getPanelById(id);
    if (!panel) {
      console.error(`패널 업데이트 실패: ID=${id} 패널을 찾을 수 없습니다.`);
      return false;
    }
    
    try {
      // 패널의 위치 및 크기 속성 업데이트
      if (props.width) {
        panel.setWidth(props.width);
      }
      
      if (props.height) {
        panel.setHeight(props.height);
      }
      
      if (props.left !== undefined && props.top !== undefined) {
        panel.setPosition(props.left, props.top);
      }
      
      // 제목 업데이트
      if (props.title) {
        panel.setTitle(props.title);
      }
      
      // URL 업데이트
      if (props.url) {
        panel.setURL(props.url);
      }
      
      console.log(`패널 속성 업데이트됨: ID=${id}`);
      this.saveConfig();
      return true;
    } catch (error) {
      console.error(`패널 속성 업데이트 중 오류 발생: ${error.message}`);
      return false;
    }
  }
}

// PanelManager 인스턴스 생성 및 전역으로 노출 (또는 모듈로 export)
// 다른 모듈에서 panelManager.createPanel 등으로 접근 가능하도록 함
const panelManager = new PanelManager();
window.panelManager = panelManager; // 전역으로 설정 (다른 접근 방법 고려 가능)

export { DraggablePanel, panelManager }; 